import { ref } from 'vue'
import RecordeApi from '~~/package/apis/recorde/RecordeApi'
import type PreSignedUrlParamsRequest from '~~/package/apis/recorde/request/PreSignedUrlParamsRequest'

export const useWebcamRecordingService = () => {
  const isRecording = ref(false)
  let mediaRecorder: MediaRecorder | null = null
  let stream: MediaStream | null = null
  let chunks: Blob[] = []
  let uploadParams: PreSignedUrlParamsRequest | null = null

  const releaseStream = () => {
    stream?.getTracks().forEach((t) => t.stop())
    stream = null
  }

  const start = async (params?: PreSignedUrlParamsRequest): Promise<boolean> => {
    if (isRecording.value) return true   // 재진입 시 기존 녹화 스트림을 그대로 유지
    uploadParams = params ?? uploadParams
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 640, height: 480 }, audio: false,
      })
      chunks = []
      const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
        ? 'video/webm;codecs=vp9' : 'video/webm'
      mediaRecorder = new MediaRecorder(stream, { mimeType })
      mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data) }
      mediaRecorder.onerror = () => {
        releaseStream()
        isRecording.value = false
      }
      mediaRecorder.start(1000)
      isRecording.value = true
      return true
    } catch {
      // 권한 거부/장치 없음/레코더 초기화 실패 — face 시그널은 중립 처리(잡에서 파일 부재로 인지)
      releaseStream()
      mediaRecorder = null
      return false
    }
  }

  const stop = async (): Promise<void> => {
    if (!mediaRecorder || mediaRecorder.state === 'inactive') {
      releaseStream()
      isRecording.value = false
      return
    }
    const blob = await new Promise<Blob>((resolve) => {
      mediaRecorder!.onstop = () => resolve(new Blob(chunks, { type: mediaRecorder!.mimeType }))
      mediaRecorder!.stop()
    })
    releaseStream()
    isRecording.value = false
    if (uploadParams && blob.size > 0) {
      try {
        const api = new RecordeApi()
        const url = await api.getPreSignedUrl({ ...uploadParams, file_name: 'webcam-recording.webm' })
        await api.uploadVideo(url, blob)
      } catch (e) {
        console.warn('webcam upload failed', e instanceof Error ? e.message : String(e))
      }
    }
  }

  return { start, stop, isRecording }
}
